import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/solid";

/**
 * PeriodLockCell — replaces the Edit/Delete controls on a row whose date falls
 * inside a closed period.
 *
 * A row that is entirely locked gets one lock icon, not four greyed-out buttons:
 * disabled controls teach when *some* actions remain, but a wall of them is just
 * noise. Callers therefore swap the whole cell —
 * `locked ? <PeriodLockCell /> : <existing buttons />` — and never render both.
 *
 * Filled icon (vs. the banner's outline) because this row is genuinely locked,
 * not merely informed. The static cue is deliberately threefold — colour, icon
 * and label — so the state never depends on motion or hover alone.
 *
 * `onView` keeps a read-only affordance available where one existed before;
 * viewing was never a write.
 */
const PeriodLockCell = ({ onView, className = "", label }) => {
  const { t } = useTranslation();

  const text = t(label || "accounting.period.lockedRow");

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Native title is this codebase's only tooltip mechanism. */}
      <span className="inline-flex items-center text-amber-500" title={text}>
        <LockClosedIcon className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">{text}</span>
      </span>
      {onView && (
        <button
          type="button"
          onClick={onView}
          className="text-xs font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:underline"
        >
          {t("common.view")}
        </button>
      )}
    </div>
  );
};

export default PeriodLockCell;
